import * as React from "react"

import Breadcrumbs from "#React/ui/Breadcrumbs"
import Icon from "#React/ui/Icon/Icon"
import Tabs from "#React/ui/Tabs"
import { cn } from "#Utils/cn"

type PageComponent = React.FC<React.PropsWithChildren<{ className?: string }>> & {
  Bar: typeof Bar
  Tabs: typeof PageTabs
  Content: typeof Content
  TwoColumn: typeof TwoColumn
  LeftColumn: typeof LeftColumn
  RightColumn: typeof RightColumn
}

type PageTab = {
  value: string
  label: React.ReactNode
  href?: string
  to?: string
  params?: Record<string, string>
  search?: Record<string, unknown>
  onClick?: () => void
}

type PageTabsProps = {
  tabs: PageTab[]
  activeTab?: string
  className?: string
  children?: React.ReactNode
  onValueChange?: (value: string) => void
}

type SharedTabsProps = Omit<PageTabsProps, "activeTab"> & {
  activeTab: string
}

const SharedTabs = Tabs as unknown as React.ComponentType<SharedTabsProps>

const Page = (({ children, className }: React.PropsWithChildren & { className?: string }) => {
  return <section className={cn("h-full flex flex-col", className)}>{children}</section>
}) as PageComponent

const normalizeIconName = (icon?: string) => {
  if (!icon) {
    return undefined
  }

  return icon.includes("fa-") ? icon : `fa fa-${icon}`
}

const Bar = ({
  icon,
  title,
  subtitle,
  className,
  noBottomMargin = false,
  actions,
  breadcrumbs,
}: {
  actions?: React.ReactNode
  className?: string
  noBottomMargin?: boolean
  icon?: string
  title?: React.ReactNode
  subtitle?: React.ReactNode
  breadcrumbs?: {
    label: React.ReactNode
    href?: string
    to?: string
    params?: Record<string, string>
    search?: Record<string, unknown>
  }[]
}) => (
  <div
    className={cn(
      "c-pagebar mt-0",
      !noBottomMargin && "mb-4 lg:mb-6",
      "py-3 lg:py-4 px-4 lg:px-8 border-b border-edge-default",
      className,
    )}
  >
    <div className="c-pagebar__header flex-1 max-w-full md:min-w-0">
      <h1 className="c-pagebar__title">
        {icon && (
          <span className="hidden md:inline-flex">
            <Icon name={normalizeIconName(icon)} size="sm" className="c-pagebar__title-icon" />
          </span>
        )}
        {breadcrumbs ? (
          <Breadcrumbs>
            {breadcrumbs?.map(({ label, href, to, params, search }, index) => (
              <Breadcrumbs.Item
                key={typeof label === "string" ? label : index}
                href={href}
                to={to}
                params={params}
                search={search}
              >
                {label}
              </Breadcrumbs.Item>
            ))}
            {title && <Breadcrumbs.Item>{title}</Breadcrumbs.Item>}
          </Breadcrumbs>
        ) : (
          title
        )}
      </h1>
      {subtitle && <h2 className="c-pagebar__subtitle">{subtitle}</h2>}
    </div>
    {actions && <div className="c-pagebar__actions">{actions}</div>}
  </div>
)

const PageTabs = ({ tabs, activeTab, className, children, onValueChange }: PageTabsProps) => {
  const resolvedActiveTab = activeTab ?? tabs[0]?.value ?? ""

  return (
    <SharedTabs
      tabs={tabs}
      activeTab={resolvedActiveTab}
      className={cn("px-4 lg:px-8 mb-4 lg:mb-6 max-md:overflow-auto", className)}
      onValueChange={onValueChange}
    >
      {children}
    </SharedTabs>
  )
}

const Content = ({
  fill = false,
  className,
  children,
}: React.PropsWithChildren<{ fill?: boolean; className?: string }>) => (
  <div
    className={cn(
      "px-4 lg:px-8 pb-4 lg:pb-8",
      fill ? "flex-1 md:min-h-0 flex flex-col" : "shrink-0",
      className,
    )}
  >
    {children}
  </div>
)

const TwoColumn = ({ children }: React.PropsWithChildren) => (
  <div className="px-4 md:px-0 md:flex w-full flex-1 md:overflow-hidden">{children}</div>
)

const LeftColumn = ({ children }: React.PropsWithChildren) => (
  <div className="md:w-80 flex-auto md:overflow-y-auto md:pl-8 md:pb-8">{children}</div>
)

const RightColumn = ({ children }: React.PropsWithChildren) => (
  <div className="md:w-88 md:pl-6 md:pr-8 md:pb-8 md:overflow-y-auto">{children}</div>
)

Page.Bar = Bar
Page.Tabs = PageTabs
Page.Content = Content
Page.TwoColumn = TwoColumn
Page.LeftColumn = LeftColumn
Page.RightColumn = RightColumn

export default Page
